import { AppLayout, SideNavigation, BreadcrumbGroup, Flashbar, Toggle, Link, SpaceBetween } from '@cloudscape-design/components';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, matchPath } from 'react-router';
import { useMemo, useState } from 'react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import { ArbiterClientProvider } from './context/ArbiterClientContext';
import { RulesListPage } from './pages/rules/RulesListPage';
import { RuleDetailPage } from './pages/rules/RuleDetailPage';
import { CreateRulePage } from './pages/rules/CreateRulePage';
import { EditRulePage } from './pages/rules/EditRulePage';
import { EvaluationsListPage } from './pages/evaluations/EvaluationsListPage';
import { EvaluationDetailPage } from './pages/evaluations/EvaluationDetailPage';
import { DashboardPage } from './pages/DashboardPage';
import { CreateEvaluationPage } from './pages/evaluations/CreateEvaluationPage';
import { GuidanceListPage } from './pages/guidance/GuidanceListPage';
import { GuidanceDetailPage } from './pages/guidance/GuidanceDetailPage';

interface BreadcrumbItem { text: string; href: string }

const routePatterns = [
  '/rules/:ruleName/edit',
  '/rules/create',
  '/rules/:ruleName',
  '/rules',
  '/evaluations/create',
  '/evaluations/:evaluationId',
  '/evaluations',
  '/guidance',
  '/guidance/*',
  '/',
] as const;

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ text: 'Helianthus Arbiter', href: '/' }];

  for (const pattern of routePatterns) {
    const match = matchPath(pattern, pathname);
    if (!match) continue;

    if (pattern.startsWith('/rules')) {
      items.push({ text: 'Rules', href: '/rules' });
      const ruleName = match.params.ruleName;
      if (ruleName) {
        items.push({ text: decodeURIComponent(ruleName), href: `/rules/${ruleName}` });
        if (pattern.endsWith('/edit')) {
          items.push({ text: 'Edit', href: pathname });
        }
      } else if (pattern === '/rules/create') {
        items.push({ text: 'Create Rule', href: pathname });
      }
    } else if (pattern.startsWith('/evaluations')) {
      items.push({ text: 'Evaluations', href: '/evaluations' });
      const evaluationId = match.params.evaluationId;
      if (evaluationId) {
        items.push({ text: decodeURIComponent(evaluationId), href: pathname });
      } else if (pattern === '/evaluations/create') {
        items.push({ text: 'Create Evaluation', href: pathname });
      }
    } else if (pattern.startsWith('/guidance')) {
      items.push({ text: 'Guidance', href: '/guidance' });
      // For /guidance/* (detail page), extract the path after /guidance/
      const guidancePath = match.params['*'];
      if (guidancePath) {
        items.push({ text: guidancePath, href: pathname });
      }
    } else if (pattern === '/') {
      // Root — just the home breadcrumb, already added
    }
    break;
  }

  return items;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  // Will be used in later tasks for error handling
  const [notifications] = useState<any[]>([]);
  const breadcrumbs = useMemo(() => buildBreadcrumbs(location.pathname), [location.pathname]);

  const savedTheme = localStorage.getItem('helianthus-theme');
  const [darkMode, setDarkMode] = useState(savedTheme !== 'light');

  const activeHref = location.pathname;

  return (
    <>
      <AppLayout
        toolsHide={true}
        navigation={
          <>
            <SideNavigation
              header={{ text: 'Helianthus Arbiter', href: '/' }}
              activeHref={activeHref}
              onFollow={(event) => {
                if (event.detail.external) {
                  return;
                }
                event.preventDefault();
                navigate(event.detail.href);
              }}
              items={[
                { type: 'section', text: 'Rules', items: [
                  { type: 'link', text: 'All Rules', href: '/rules' },
                  { type: 'link', text: 'Create Rule', href: '/rules/create' },
                ]},
                { type: 'section', text: 'Evaluations', items: [
                  { type: 'link', text: 'All Evaluations', href: '/evaluations' },
                  { type: 'link', text: 'Create Evaluation', href: '/evaluations/create' },
                ]},
                { type: 'section', text: 'Guidance', items: [
                  { type: 'link', text: 'Browse Guidance', href: '/guidance' },
                ]},
              ]}
            />
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border-divider-default, #414d5c)' }}>
              <Toggle checked={darkMode} onChange={({ detail }) => { setDarkMode(detail.checked); applyMode(detail.checked ? Mode.Dark : Mode.Light); localStorage.setItem('helianthus-theme', detail.checked ? 'dark' : 'light'); }}>
                Dark mode
              </Toggle>
            </div>
            <div style={{ padding: '8px 20px' }}>
              <SpaceBetween size="xs">
                <Link
                  href="https://console.harmony.a2z.com/mcp-registry/server/arbiter-mcp"
                  external
                  fontSize="body-s"
                >
                  Arbiter MCP
                </Link>
                <Link
                  href="https://w.amazon.com/bin/view/HelianthusArbiter"
                  external
                  fontSize="body-s"
                >
                  Documentation
                </Link>
              </SpaceBetween>
            </div>
          </>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={breadcrumbs}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
          />
        }
        notifications={<Flashbar items={notifications} />}
        content={
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/rules" element={<RulesListPage />} />
            <Route path="/rules/create" element={<CreateRulePage />} />
            <Route path="/rules/:ruleName" element={<RuleDetailPage />} />
            <Route path="/rules/:ruleName/edit" element={<EditRulePage />} />
            <Route path="/evaluations" element={<EvaluationsListPage />} />
            <Route path="/evaluations/create" element={<CreateEvaluationPage />} />
            <Route path="/evaluations/:evaluationId" element={<EvaluationDetailPage />} />
            <Route path="/guidance" element={<GuidanceListPage />} />
            <Route path="/guidance/*" element={<GuidanceDetailPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        }
      />
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ArbiterClientProvider>
        <AppContent />
      </ArbiterClientProvider>
    </BrowserRouter>
  );
}
